import React from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery } from "@apollo/client";
import { GET_TEMPLATE_STRUCTURE } from "graphql/TemplateGroupQueries";
import { Step, Tooltip, Stack, Typography } from "@mui/material";
import {
  StyledDrawer,
  StyledStepContent,
  StyledStepContentDiv,
  StyledStepLabel,
  StyledStepper,
} from "./SideBarStyledComponents";
import { getTemplatePages, getActiveStep } from "shared/utils";
import {
  SIMULATION,
  PRESCRIPTION,
  VOLUMING,
  INFORMATION,
  CYCLE,
  PEER_REVIEW,
  BULK_EDIT_REVIEW,
  TEMPLATE_PAGE,
} from "i18n/constants";
import {
  PrescriptionTemplateInterface,
  RuleInterface,
  SiteGroupTemplateInterface,
  SiteTemplateInterface,
} from "interfaces/templates";

interface ParamTypes {
  templateId: string;
}

interface Props {
  valid: boolean;
  showPeerReview?: boolean;
  isRo: boolean;
  isBulkEdit?: boolean;
}

const getSimulationGroupName = (siteGroup: SiteGroupTemplateInterface) => {
  return siteGroup.siteTemplate
    .map((site: SiteTemplateInterface) => site.site.name)
    .join(", ");
};

const getPrescriptionGroupName = (
  prescription: PrescriptionTemplateInterface,
) => {
  return prescription.siteGroupTemplate
    .map((siteGroup: SiteGroupTemplateInterface) =>
      siteGroup.siteTemplate.map(
        (site: SiteTemplateInterface) => site.site.name,
      ),
    )
    .flat()
    .join(", ");
};

const SideBarNav = ({ showPeerReview, valid, isRo, isBulkEdit }: Props) => {
  const pages = getTemplatePages(isRo, showPeerReview, isBulkEdit);

  const navigate = useNavigate();
  const { templateId } = useParams<keyof ParamTypes>();

  const { loading, error, data } = useQuery(GET_TEMPLATE_STRUCTURE, {
    variables: { templateId },
  });
  const activeStepIndex = pages.indexOf(getActiveStep());
  const prescriptions = data?.template?.prescriptionTemplate;
  const siteGroups = data?.template?.prescriptionTemplate
    .map((prescription: any) => prescription.siteGroupTemplate)
    .flat();
  const sites = data?.template?.prescriptionTemplate
    .map((prescription: any) =>
      prescription.siteGroupTemplate
        .map((siteGroup: any) =>
          siteGroup.siteTemplate.map((site: any) => site),
        )
        .flat()
        .sort(
          (a: SiteTemplateInterface, b: SiteTemplateInterface) =>
            Number(a.id) - Number(b.id),
        ),
    )
    .flat();

  if (loading || !data || !siteGroups) return <div>Loading</div>;
  if (error) return <div>Error</div>;

  const navigateToPage = (url: string) => {
    const pathPrefix = isBulkEdit ? "bulk_edit" : "template";
    if (valid || url === "") navigate(`/${pathPrefix}/${templateId}/${url}`);
  };

  // During bulk edit, display the names of selected templates (stored in sessionStorage by the selection page)
  const bulkEditNames: string[] = isBulkEdit
    ? (JSON.parse(sessionStorage.getItem("bulkEditList") || "[]") || [])
        .map((t: any) => t?.name)
        .filter(Boolean)
    : [];

  const getStepContent = (page: string, index: number) => {
    switch (page) {
      case SIMULATION:
        return siteGroups.map((siteGroup: any, index: number) => (
          <Tooltip
            key={`${index}-${siteGroup.id}`}
            title={getSimulationGroupName(siteGroup)}
            placement="right"
          >
            <StyledStepContentDiv
              onClick={() => navigateToPage(`simulation/${siteGroup.id}`)}
            >
              {`Sim Group ${index + 1}: ${getSimulationGroupName(siteGroup)}`}
            </StyledStepContentDiv>
          </Tooltip>
        ));
      case PRESCRIPTION:
        return sites.map((site: SiteTemplateInterface, index: number) => (
          <StyledStepContentDiv
            key={`${index}-${site.id}`}
            onClick={() => navigateToPage(`prescription/${site.id}`)}
          >
            {site?.site?.name}
          </StyledStepContentDiv>
        ));
      case VOLUMING:
        return isBulkEdit
          ? ""
          : prescriptions.map((prescription: any, index: number) => (
              <StyledStepContentDiv
                key={`${index}-${prescription.id}`}
                onClick={() => navigateToPage(`voluming/${prescription.id}`)}
              >
                {`Prescription ${index + 1}`}
              </StyledStepContentDiv>
            ));
      case INFORMATION:
      case CYCLE:
      case PEER_REVIEW:
      case BULK_EDIT_REVIEW:
      case TEMPLATE_PAGE:
        return "";
      default:
        return "Unknown step";
    }
  };
  const getStepLabelNav = (page: string) => {
    switch (page) {
      case TEMPLATE_PAGE:
        return "";
      case INFORMATION:
        return "information/";
      case SIMULATION:
        return `simulation/${siteGroups[0]?.id}`;
      case PRESCRIPTION:
        return `prescription/${siteGroups[0]?.siteTemplate[0]?.id}`;
      case VOLUMING:
        return prescriptions?.[0]?.id ? `voluming/${prescriptions[0].id}` : "";
      case PEER_REVIEW:
        return "peerReview";
      case BULK_EDIT_REVIEW:
        return prescriptions?.[0]?.id
          ? `review/${prescriptions[0].id}`
          : "review";
      case CYCLE:
        return "cycle/0";
      default:
        return "";
    }
  };

  return (
    <StyledDrawer>
      {/* Template Name Section */}
      <Stack
        direction="column"
        alignItems="flex-start"
        padding={2}
        borderBottom={1}
        borderColor={"divider"}
        sx={{ backgroundColor: "background.paper" }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          Template
        </Typography>
        {isBulkEdit && bulkEditNames.length > 0 ? (
          <Tooltip title={bulkEditNames.join(", \n ")}>
            <Typography
              variant="subtitle2"
              data-test-id="sidebar-template-name"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 10,
                WebkitBoxOrient: "vertical",
              }}
            >
              {bulkEditNames.map((n, i) => (
                <span key={`${n}-${i}`}>
                  {n}
                  {i < bulkEditNames.length - 1 ? <br /> : null}
                </span>
              ))}
            </Typography>
          </Tooltip>
        ) : (
          <Tooltip title={data?.template?.name ?? ""}>
            <Typography
              variant="subtitle2"
              data-test-id="sidebar-template-name"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {data?.template?.name || "Loading..."}
            </Typography>
          </Tooltip>
        )}
      </Stack>

      <StyledStepper
        nonLinear
        activeStep={activeStepIndex}
        orientation="vertical"
      >
        {pages.map((page: string, index: number) => {
          const isValid = valid || page === TEMPLATE_PAGE ? "true" : "false";
          const path = getStepLabelNav(page);
          return (
            <Step expanded key={index}>
              <StyledStepLabel
                valid={isValid}
                onClick={() => navigateToPage(path)}
              >
                {page}
              </StyledStepLabel>
              <StyledStepContent valid={isValid}>
                {getStepContent(page, index)}
              </StyledStepContent>
            </Step>
          );
        })}
      </StyledStepper>
    </StyledDrawer>
  );
};

export default SideBarNav;
